/**
 * SquadraSelector — dropdown delle squadre della lega.
 *
 * Usa /squadre. Auto-seleziona la squadra dell'utente loggato — sia da
 * presidente che da allenatore in seconda (Andrea 2026-09-01).
 */

import { useEffect } from "react";
import { useAuth } from "../../hooks/useAuth";
import { useMieSquadreGestite, useSquadre } from "../../hooks/useFase7";
import { tr } from "../../i18n";

interface SquadraSelectorProps {
  value: string;
  onChange: (id: string) => void;
  /** Se true, auto-seleziona la squadra di cui l'utente è presidente. Default true */
  autoSelectMine?: boolean;
  /** Label sopra il select */
  label?: string;
  /** Mostra solo le squadre dell'utente loggato (se admin → tutte) */
  onlyMine?: boolean;
  className?: string;
}

export function SquadraSelector({
  value,
  onChange,
  autoSelectMine = true,
  label = tr("Squadra"),
  onlyMine = false,
  className = "",
}: SquadraSelectorProps) {
  const { user } = useAuth();
  const { data: squadre, isLoading } = useSquadre();
  const { data: idsGestite } = useMieSquadreGestite();

  const isMia = (s: { id: string; presidente_user_id: string | null }) =>
    s.presidente_user_id === user?.id || (idsGestite ?? []).includes(s.id);

  const filtered = onlyMine ? (squadre?.filter((s) => isMia(s)) ?? []) : (squadre ?? []);

  useEffect(() => {
    if (!autoSelectMine || value || !filtered.length) return;
    const mia = filtered.find((s) => isMia(s));
    if (mia) onChange(mia.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtered, value, autoSelectMine, user, idsGestite, onChange]);

  return (
    <div className={className}>
      {label && (
        <label className="block text-xs text-fg-muted uppercase tracking-wider mb-1">{label}</label>
      )}
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={isLoading || !filtered.length}
        className="w-full px-3 py-2 bg-primary-deep border border-primary-soft rounded-lg text-fg text-sm focus:outline-none focus:ring-2 focus:ring-accent disabled:opacity-50"
      >
        {isLoading && <option>{tr("Caricamento…")}</option>}
        {!isLoading && !filtered.length && <option value="">{tr("— nessuna squadra —")}</option>}
        {!isLoading && filtered.length > 0 && (
          <>
            <option value="">{tr("— seleziona —")}</option>
            {filtered.map((s) => {
              const tua = isMia(s) ? tr(" (tua)") : "";
              return (
                <option key={s.id} value={s.id}>
                  {s.nome}
                  {tua}
                </option>
              );
            })}
          </>
        )}
      </select>
    </div>
  );
}
